import { useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { Star, Send, CheckCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `/api`;

const ReviewsPage = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: "",
    company: "",
    rating: 5,
    review_text: ""
  });

  const fetchReviews = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/reviews/approved`);
      setReviews(response.data);
    } catch {
      // fetch error
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleRatingChange = (rating) => {
    setFormData(prev => ({ ...prev, rating }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_name || !formData.review_text) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, formData);
      setSubmitted(true);
      toast.success("Thank you for your review! It will be published after approval.");
      setFormData({
        customer_name: "",
        company: "",
        rating: 5,
        review_text: ""
      });
    } catch {
      toast.error("Failed to submit review. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const StarRating = ({ rating, interactive = false, onRatingChange }) => {
    const [hoverRating, setHoverRating] = useState(0);
    
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            className={`${interactive ? 'cursor-pointer' : 'cursor-default'}`}
            onClick={() => interactive && onRatingChange && onRatingChange(star)}
            onMouseEnter={() => interactive && setHoverRating(star)}
            onMouseLeave={() => interactive && setHoverRating(0)}
            disabled={!interactive}
          >
            <svg
              className={`w-6 h-6 transition-colors ${
                star <= (hoverRating || rating) ? 'text-[#4A5D4E]' : 'text-[#E6E4DD]'
              }`}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          </button>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen" data-testid="reviews-page">
      <Helmet>
        <title>Customer Reviews | DUGARS</title>
        <meta name="description" content="Read what our clients say about DUGARS fabrics. Trusted by 200+ happy customers across the textile industry." />
        <link rel="canonical" href="https://thedugars.in/reviews" />
        <meta property="og:title" content="Customer Reviews | DUGARS" />
        <meta property="og:description" content="Read what our clients say about DUGARS fabrics." />
        <meta property="og:url" content="https://thedugars.in/reviews" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
      </Helmet>
      {/* Header */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-3xl mx-auto">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#4A5D4E] mb-4 block">
              Customer Reviews
            </span>
            <h1 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter text-[#1C1C1A]">
              What Our Customers Say
            </h1>
            <p className="text-[#6B6A65] font-light mt-6">
              Read reviews from our valued customers and share your experience with DUGARS.
            </p>
          </div>
        </div>
      </section>

      {/* Reviews Grid */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse bg-white p-8 border border-[#E6E4DD]">
                  <div className="flex gap-1 mb-4">
                    {[1, 2, 3, 4, 5].map((j) => (
                      <div key={j} className="w-5 h-5 bg-[#E6E4DD] rounded" />
                    ))}
                  </div>
                  <div className="h-20 bg-[#E6E4DD] mb-6" />
                  <div className="h-4 bg-[#E6E4DD] w-1/2 mb-2" />
                  <div className="h-3 bg-[#E6E4DD] w-1/3" />
                </div>
              ))}
            </div>
          ) : reviews.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-[#6B6A65] font-light text-lg mb-4">
                No reviews yet. Be the first to share your experience!
              </p>
            </div>
          ) : (
            <div 
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
              data-testid="reviews-grid"
            >
              {reviews.map((review) => (
                <div 
                  key={review.id} 
                  className="bg-white p-8 border border-[#E6E4DD]"
                  data-testid={`review-item-${review.id}`}
                >
                  <StarRating rating={review.rating} />
                  
                  <p className="text-[#6B6A65] font-light leading-relaxed my-6">
                    "{review.review_text}"
                  </p>
                  
                  <div className="pt-4 border-t border-[#E6E4DD]">
                    <p className="font-medium text-[#1C1C1A]">{review.customer_name}</p>
                    {review.company && (
                      <p className="text-sm text-[#6B6A65]">{review.company}</p>
                    )}
                    <p className="text-xs text-[#6B6A65] mt-2">
                      {new Date(review.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Submit Review Form */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-2xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#4A5D4E] mb-4 block">
              Share Your Experience
            </span>
            <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-normal tracking-tight text-[#1C1C1A]">
              Leave a Review
            </h2>
            <p className="text-[#6B6A65] font-light mt-4">
              Your feedback helps us improve and helps other customers make informed decisions.
            </p>
          </div>

          {submitted ? (
            <div className="text-center py-12 bg-[#F7F5F0] border border-[#E6E4DD]">
              <CheckCircle size={48} className="text-[#4A5D4E] mx-auto mb-4" />
              <h3 className="font-['Cormorant_Garamond'] text-2xl text-[#1C1C1A] mb-2">
                Thank You!
              </h3>
              <p className="text-[#6B6A65] font-light mb-6">
                Your review has been submitted and will be published after approval.
              </p>
              <button
                onClick={() => setSubmitted(false)}
                className="btn-secondary"
                data-testid="submit-another-btn"
              >
                Submit Another Review
              </button>
            </div>
          ) : (
            <form 
              onSubmit={handleSubmit} 
              className="space-y-6 bg-[#F7F5F0] p-8 md:p-12 border border-[#E6E4DD]"
              data-testid="review-form"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-[#1C1C1A] mb-2">
                    Your Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="customer_name"
                    value={formData.customer_name}
                    onChange={handleChange}
                    className="custom-input w-full"
                    placeholder="Your name"
                    required
                    data-testid="review-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1C1C1A] mb-2">
                    Company Name
                  </label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="custom-input w-full"
                    placeholder="Your company (optional)"
                    data-testid="review-company-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1C1C1A] mb-3">
                  Rating <span className="text-red-500">*</span>
                </label>
                <StarRating 
                  rating={formData.rating} 
                  interactive={true}
                  onRatingChange={handleRatingChange}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1C1C1A] mb-2">
                  Your Review <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="review_text"
                  value={formData.review_text}
                  onChange={handleChange}
                  rows={5}
                  className="custom-input w-full resize-none"
                  placeholder="Share your experience with DUGARS..."
                  required
                  data-testid="review-text-input"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="review-submit-btn"
              >
                {submitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    Submit Review
                    <Send size={18} />
                  </>
                )}
              </button>

              <p className="text-xs text-[#6B6A65] text-center">
                Reviews are moderated and will be published after approval.
              </p>
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

export default ReviewsPage;
